import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import ApiHandler from "src/api/ApiHandler"
import ErrorHandler from "src/components/errors/ErrorHandler"
import Loader from "src/components/general/Loading"
import AreaAnalysisPage from "src/components/areas/AreaAnalysisPage"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import type { AnalysisBuilderChain } from "src/components/areas/areaAnalysisDescription/analysis_builders/AnalysisBuilderChain"

interface AreaAnalysisPageServerProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loader: Loader
    areaAnalysisBuilderChain: AnalysisBuilderChain
}

export default function AreaAnalysisPageServer(props: AreaAnalysisPageServerProps) {
    const { areaId, areaSlug } = useParams<{
        areaId: string
        areaSlug?: string
    }>()
    const navigate = useNavigate()
    const [isValidating, setIsValidating] = useState(true)
    const [shouldRender, setShouldRender] = useState(false)

    useEffect(() => {
        async function validateAndRedirect() {
            if (!areaId) {
                setIsValidating(false)
                return
            }

            try {
                const areaResponse = await props.apiHandler.findArea({ id: areaId })
                const correctAreaSlug = areaResponse?.aurl

                if (!areaSlug) {
                    if (correctAreaSlug) {
                        navigate(`/area/${areaId}/${encodeURIComponent(correctAreaSlug)}`, { replace: true })
                        return
                    } else {
                        setIsValidating(false)
                        setShouldRender(true)
                        return
                    }
                }

                if (correctAreaSlug && correctAreaSlug !== areaSlug) {
                    navigate(`/area/${areaId}/${encodeURIComponent(correctAreaSlug)}`, { replace: true })
                    return
                }

                setIsValidating(false)
                setShouldRender(true)
            } catch (error) {
                console.error("Error validating area URL:", error)
                setIsValidating(false)
                setShouldRender(true)
            }
        }

        validateAndRedirect()
    }, [areaId, areaSlug, navigate, props.apiHandler])

    if (!areaId) {
        return (
            <div>
                Missing area id. Try a URL like <code>/area/{`{areaId}`}/{`{areaSlug}`}</code>
            </div>
        )
    }

    if (isValidating) {
        return props.loader.render("Loading area")
    }

    if (!shouldRender) {
        return null
    }

    return (
        <AreaAnalysisPage
            areaId={areaId}
            stateHandler={props.stateHandler}
            apiHandler={props.apiHandler}
            errorHandler={props.errorHandler}
            loader={props.loader}
            areaAnalysisBuilderChain={props.areaAnalysisBuilderChain}
        />
    )
}
